import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCurrentSession } from "@/services/auth";
import type { AuthSession } from "@/types";
import { ApiError } from "@/services/api";
import {
  AUTH_SESSION_QUERY_KEY,
  AuthContext,
  isCompleteSession,
  type AuthContextValue,
  type AuthStatus,
} from "./authContextValue";

/**
 * AuthProvider — wraps the app with the authenticated-session context.
 *
 * Backed by the TanStack Query cache so /me is fetched once + revalidated
 * on focus and on a slow poll. The query is allowed to fail (401) — the
 * failure is interpreted as ``unauthenticated`` rather than an error
 * condition, so the public auth pages render normally.
 */

/**
 * Slow poll for the session payload (ENG-BILLING-TIER-STALE-CLIENT-CACHE).
 *
 * ``workspace.tier`` rides this payload, and ENG-BILLING-TIER-CHURN-SYNC made
 * it degrade SERVER-SIDE — on a Stripe webhook, or on the daily
 * ``billing_dunning_lapse_sweep``. There is no client mutation to
 * ``invalidateQueries`` off, so without a poll the only refresh triggers were
 * a window blur→focus cycle and a hard reload: a tab that stays continuously
 * focused kept showing Pro+ affordances for the rest of its life.
 *
 * 5 minutes, matching ``useImageTrial``, deliberately NOT the 60s
 * ``staleTime``: every gated action is independently re-checked server-side
 * (403 ``tier_not_eligible`` before any write), so the stale badge is
 * cosmetic and does not justify a per-minute /me poll from every open tab.
 */
const AUTH_SESSION_REFETCH_MS = 5 * 60_000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const query = useQuery<AuthSession | null, Error>({
    queryKey: AUTH_SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        return await fetchCurrentSession();
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    // A transient /me failure (5xx, network blip, cold backend) must not
    // read as "signed out": retry non-401 failures twice before the query
    // settles in error. A 401 never retries - it is the definitive
    // "no session" answer (ENG-NEW-USER-FUNNEL-PROOF W2-T4 e).
    retry: (n, err) =>
      !(err instanceof ApiError && err.status === 401) && n < 2,
    staleTime: 60_000,
    // Gate focus-refetch on whether a session is cached. Without the
    // gate, an unreachable backend (data === undefined, query in error
    // state) loses `staleTime`'s no-op behaviour and every focus event
    // re-fires `/me` — preview / form interactions can trigger a 10+
    // request storm that exhausts the browser socket pool before the
    // user can see the network-error banner. The gate preserves the
    // session-expiry-on-focus signal for authenticated users (data
    // present → refetches normally) while suppressing the storm for
    // unauthenticated + error states (no cached session to lose).
    refetchOnWindowFocus: (q) => q.state.data != null,
    // Gated on a cached session for the SAME reason as the focus refetch
    // above: an ungated interval would poll a dead backend forever and
    // re-create the socket-pool storm that gate was added to stop.
    refetchInterval: (q) =>
      q.state.data != null ? AUTH_SESSION_REFETCH_MS : false,
  });

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: AUTH_SESSION_QUERY_KEY });
  }, [queryClient]);

  // Single validation chokepoint for the session envelope.
  //
  // It MUST live here rather than in `queryFn`: the /me fetch is only one
  // of eight writers into AUTH_SESSION_QUERY_KEY — login, signup, magic-link
  // verify, Google sign-in and reset-password all `setQueryData` the session
  // directly (hooks/useAuth.ts), as does accepting a workspace invitation
  // (hooks/useWorkspaceInvitations.ts). Validating inside `queryFn` would
  // leave seven back doors around the guard; validating on read covers every
  // writer, present and future, with one check.
  //
  // An incomplete envelope is treated as NO session, which makes it
  // indistinguishable from a 401 downstream: ProtectedRoute redirects to
  // /login instead of letting `session?.workspace.workspace_id` throw
  // inside SelectedAppProvider and white-screen the whole shell.
  const session = useMemo(
    () => (isCompleteSession(query.data) ? query.data : null),
    [query.data],
  );

  // A malformed 200 is unreachable from the backend contract, so it always
  // means something upstream is broken. Bouncing to /login is the correct
  // UX but a SILENT logout loop is harder to diagnose than the loud error
  // boundary it replaces — leave a breadcrumb, matching ErrorBoundary's
  // console.error diagnostics idiom.
  useEffect(() => {
    if (query.data != null && !isCompleteSession(query.data)) {
      console.error(
        "Discarded a malformed /api/v1/auth/me session envelope (missing or " +
          "invalid `user` / `workspace`); treating the request as " +
          "unauthenticated. This response cannot come from the backend " +
          "contract — suspect a proxy, cache or service worker.",
        query.data,
      );
    }
  }, [query.data]);

  const status: AuthStatus = useMemo(() => {
    if (query.isLoading) return "loading";
    return session ? "authenticated" : "unauthenticated";
  }, [query.isLoading, session]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      isAuthenticated: status === "authenticated",
      refresh,
    }),
    [status, session, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
